import { Log_Entry, LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';
import { logEntryHoldsUserData } from '@cf-shared/data-model/log-entry-user-data';

/**
 * The delete-boundary backstop for schedule regeneration (see
 * design-docs/sync-integrity.md).
 *
 * This is the last thing that runs before `generateBacklogInMemoryAsync` saves a
 * rebuilt range of days. The rebuild marks every entry in that range as deleted
 * and revives only the ones it decides to keep, so anything the rebuild missed
 * is about to be written to the database as a deletion. This module asks, one
 * last time, whether each of those entries holds something the user put there —
 * and keeps it if it does.
 *
 * It is a module of its own, free of Angular and of the database, so that it can
 * be unit tested — the same reason `user-db.migrations.ts` and
 * `user-db.task-status.ts` sit beside the service. The loop used to live inside
 * `generateBacklogInMemoryAsync`, which needs the database wrapper, the schedule
 * singleton and the plan solver to run at all, so nothing could reach it.
 */

/** One entry the backstop kept from being deleted. Carries no user data — only
 *  the fields the `schedule-regen-blocked-completed-delete` report sends — so it
 *  is safe to hand straight to telemetry. */
export interface BlockedDeleteInfo {
  id: string;
  type: LogRecordType;
  state: LogRecordState;
  time_utc: string;
}

/**
 * Keep every entry that is about to be saved as a deletion while it still holds
 * the user's own data, and say which ones those were.
 *
 * BOTH ARRAYS ARE MODIFIED IN PLACE, which is what the caller needs:
 *  - the entry's `_deleted` flag is removed, so the save writes it as a live
 *    document rather than as a deletion. It stays in `entriesToSave`, because a
 *    revived body that is never written leaves the deletion standing on disk;
 *  - if the rebuilt list does not already carry that entry, it is added, so the
 *    caller's own return value shows the day as the user will see it.
 *
 * The returned list is for reporting only. Every element of it is a near-miss:
 * the rebuild was about to lose something, which is a case the shared-layer
 * sweep in `generateScheduleLogEntries` did not cover, and somebody should look
 * at why. The caller reports it through `trackSilentProblem`.
 *
 * # Why this asks the shared helper rather than the entry's state field
 *
 * Until #4840's follow-up this loop decided with `e.state !== Scheduled`, which
 * reads as "anything that is not an untouched placeholder". That is wrong for a
 * workout: one can be STORED saying `Scheduled` while holding every set the user
 * finished. So the shape #4840 was about walked straight through the last thing
 * that could have caught it — not kept, and not reported either, because the
 * report only fires for entries this loop keeps.
 *
 * `logEntryHoldsUserData` is the same question the shared-layer sweep asks, and
 * asking it here is what stops the two answering differently. For weigh-ins and
 * weekly check-ups it gives exactly the answer the old field test gave; for a
 * workout it additionally asks whether the document holds a set the user
 * concluded; for a meal, whether it holds any ingredient or non-zero nutrient
 * (df-c87743b6cc87); and a wake-up or bedtime entry the app ticked `Done` itself
 * holds no user data (#6431), so a second copy of one is let go here too.
 */
export function reviveEntriesHoldingUserData(
  entriesToSave: Log_Entry[],
  scheduledLogEntries: Log_Entry[],
): BlockedDeleteInfo[] {
  const blocked: BlockedDeleteInfo[] = [];
  // for every entry the rebuild is about to write away as a deletion
  for (const entry of entriesToSave) {
    if (!entry._deleted) continue;
    // if it still holds something the user put there, this deletion is a bug
    if (!logEntryHoldsUserData(entry)) continue;
    delete entry._deleted;
    blocked.push({
      id: entry._id,
      type: entry.type,
      state: entry.state,
      time_utc: entry.time_utc,
    });
    if (!scheduledLogEntries.find(s => s._id === entry._id)) {
      scheduledLogEntries.push(entry);
    }
  }
  return blocked;
}
